import { OrderService } from '@/lib/services/order'
import {
  Contact,
  CreateOrderRequest,
  MasterOrder,
  MasterOrderStatus,
} from '@/types'
import { useRouter } from 'next/router'
import { useState, useEffect, createContext, useMemo, useContext } from 'react'
import { ulid } from 'ulid'
import { ExpiredOrderModal } from '@/components/order/expired-order-modal'
import { addMinutes, differenceInSeconds, isFuture } from 'date-fns'
import useAuth from './useAuth'

interface ICartContextProps {
  masterOrder: MasterOrder | undefined
  addToCart: (orderRequest: CreateOrderRequest) => Promise<MasterOrder>
  clearCart: () => void
  removeOrder: (orderId: number) => void
  markMasterOrderAsExpired: () => void
  updateContacts: (contact: Contact, source: string) => Promise<void>
  totalAmount: number
  idempotency: string
  resetIdempotencyToken: () => void
  setDrawerOpen: (open: boolean) => void
  isDrawerOpen: boolean
  unlockTimer: string | undefined
}

export const CartContext = createContext<ICartContextProps>(
  {} as ICartContextProps
)

const GUEST_IDEMPOTENCY = 'ORDER_IDEMPOTENCY'

const isValidMasterOrder = (masterOrder: MasterOrder) => {
  return (
    masterOrder.status === MasterOrderStatus.Cart &&
    isFuture(addMinutes(masterOrder.createdAt, 30))
  )
}

export const CartContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [masterOrder, setMasterOrder] = useState<MasterOrder>()
  const [orderIdempotency, setOrderIdempotency] = useState<string>()
  const [open, setOpen] = useState(false)
  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false)
  const router = useRouter()
  const { user } = useAuth()

  const [unlockTimer, setUnlockTimer] = useState<string>()

  useEffect(() => {
    const idempotency = localStorage.getItem(GUEST_IDEMPOTENCY) ?? ulid()

    setOrderIdempotency(idempotency)
    localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
  }, [])

  useEffect(() => {
    // only fetch cart in app pages
    if (
      !router.pathname.startsWith('/app') &&
      !router.pathname.startsWith('/create-order')
    ) {
      return
    }

    const initCartItems = async (idempotency: string) => {
      const masterOrder = await OrderService.getMasterOrder(idempotency)

      if (!masterOrder) {
        setMasterOrder(undefined)
        console.info("It's a new order")
        return null
      }

      // MasterOrder already placed or if the masterOrder is expired
      if (!isValidMasterOrder(masterOrder)) {
        console.info('Resetting idempotency token')
        resetIdempotencyToken()

        return null
      }

      console.info('Valid master order')
      setMasterOrder(masterOrder)
    }

    if (orderIdempotency) {
      initCartItems(orderIdempotency)
    }
  }, [orderIdempotency, router.pathname])

  useEffect(() => {
    if (!masterOrder?.orders?.[0]) {
      return
    }

    function formatSecondsToMinutes(seconds: number) {
      const minutes = Math.floor(seconds / 60) // Calculate whole minutes
      const remainingSeconds = seconds % 60 // Calculate remaining seconds
      return `${String(minutes).padStart(2, '0')}:${String(
        remainingSeconds
      ).padStart(2, '0')}`
    }

    const interval = setInterval(() => {
      const seconds =
        60 * 30 -
        differenceInSeconds(new Date(), new Date(masterOrder?.createdAt))

      if (seconds <= 0) {
        markMasterOrderAsExpired()
      }
      setUnlockTimer(formatSecondsToMinutes(seconds))
    }, 1000)

    return () => clearInterval(interval)
  }, [masterOrder])

  const addToCart = async (
    orderRequest: CreateOrderRequest
  ): Promise<MasterOrder> => {
    let idempotency = orderIdempotency!

    let masterOrder = await OrderService.getMasterOrder(idempotency)

    // MasterOrder already placed or if the masterOrder is expired
    if (masterOrder && !isValidMasterOrder(masterOrder)) {
      idempotency = ulid()
      setOrderIdempotency(idempotency)
      localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
    }

    // Add order to the masterOrder
    masterOrder = await OrderService.createOrder(idempotency, {
      creatorId: user?.id,
      ...orderRequest,
    })

    setMasterOrder(masterOrder)

    return masterOrder
  }

  const removeOrder = async (orderId: number) => {
    try {
      await OrderService.removeOrder(orderIdempotency!, orderId)
    } catch (e) {}
    setMasterOrder({
      ...masterOrder,
      orders: (masterOrder?.orders ?? []).filter(
        (order) => order.id !== orderId
      ),
    } as MasterOrder)
  }

  const markMasterOrderAsExpired = async () => {
    try {
      await OrderService.markMasterOrderAsExpired(masterOrder!.id)
      setIsExpiredModalOpen(true)
    } catch (e) {}
    setMasterOrder({} as MasterOrder)
  }

  const updateContacts = async (contact: Contact, source: string) => {
    setMasterOrder({ ...masterOrder, customer: contact } as MasterOrder)

    await OrderService.updateContacts(masterOrder!.id, contact, source)
  }

  const totalAmount = useMemo(
    () =>
      masterOrder?.orders?.reduce(
        (prev, current) => prev + current.amount,
        0
      ) ?? 0,
    [masterOrder]
  )

  const clearCart = () => {}

  const resetIdempotencyToken = () => {
    const idempotency = ulid()
    localStorage.setItem(GUEST_IDEMPOTENCY, idempotency)
    setOrderIdempotency(idempotency)

    return idempotency
  }

  return (
    <CartContext.Provider
      value={{
        masterOrder,
        addToCart,
        markMasterOrderAsExpired,
        removeOrder,
        clearCart,
        totalAmount,
        updateContacts,
        idempotency: orderIdempotency ?? '',
        resetIdempotencyToken,
        setDrawerOpen: (open: boolean) => setOpen(open),
        isDrawerOpen: open,
        unlockTimer,
      }}
    >
      {children}
      <ExpiredOrderModal
        isOpen={isExpiredModalOpen}
        onClose={() => setIsExpiredModalOpen(false)}
      />
    </CartContext.Provider>
  )
}

const useCart = () => useContext(CartContext)

export default useCart
